import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOffline } from '@/hooks/useOffline';
import { useClinic } from '@/stores/ClinicContext';
import { Bell, Menu, PanelLeftClose, PanelLeftOpen, Search, WifiOff, X, Building2, ChevronDown, ChevronsLeftRight, ChevronsRightLeft, Maximize2, Minimize2, MoreHorizontal, CalendarDays, Undo2, Check, LogOut } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { GlobalPatientSearch } from './GlobalPatientSearch';
import { AgendaChartToggle, useAgendaChartTarget } from './AgendaChartToggle';
import { useTopbarSlotNeed, useTopbarSlotRef } from './TopbarSlot';
import { useElementWidth } from '@/hooks/useElementWidth';
import { DITAR_TOPO, MICS_TOPO, topbarCollapseLevel } from '@/utils/fichaHeaderFit';
import { useDensity } from '@/stores/DensityContext';
import { useFocusMode } from '@/stores/FocusModeContext';
import { SecretaryBriefingService } from '@/services/firebase/secretaryBriefing';
import { canSee } from '@/hooks/usePendencias';
import { Role } from '@/types/auth';
import { MicsNoTopo } from '@/components/voice/MicsNoTopo';
import { TopbarDictateButton } from './TopbarDictateButton';
import { isVoiceEnabled } from '@/services/voice/flag';

interface TopbarProps {
  /** Abre o drawer mobile da sidebar. */
  onMenuClick?: () => void;
  /** Toggla o modo colapsado (só ícones) da sidebar no desktop. */
  onDesktopToggle?: () => void;
  /** Estado atual do colapso desktop — define o ícone do botão. */
  isDesktopCollapsed?: boolean;
}

export const Topbar = ({ onMenuClick, onDesktopToggle, isDesktopCollapsed }: TopbarProps) => {
  const navigate = useNavigate();
  const { logout, clinicId, role } = useAuth();
  const { isOnline, syncPending } = useOffline();
  const { units, accessibleUnits, currentUnitId, setCurrentUnitId, setAllUnitsView } = useClinic();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [unitDropdownOpen, setUnitDropdownOpen] = useState(false);
  const [briefingCount, setBriefingCount] = useState(0);
  const currentUnit = units.find((u) => u.id === currentUnitId);

  // Sino mostra briefings pendentes (atendimentos finalizados que precisam
  // de orientação à paciente). Só assina pra quem tem permissão — médico
  // não vê o badge, mesmo passando pela mesma Topbar.
  useEffect(() => {
    if (!clinicId || !canSee(role, 'BRIEFING_NEEDED')) {
      setBriefingCount(0);
      return;
    }
    const unsub = SecretaryBriefingService.subscribePendingBriefings(
      clinicId,
      (items) => setBriefingCount(items.length),
    );
    return () => unsub();
  }, [clinicId, role]);

  const hasBriefings = briefingCount > 0;
  const briefingLabel = briefingCount > 99 ? '99+' : String(briefingCount);

  // Ref do slot central — a ficha do paciente injeta aqui o cabeçalho do
  // paciente (desktop), preenchendo o espaço antes vazio ao lado da busca.
  const setSlotRef = useTopbarSlotRef();

  // Densidade Compacto: a busca global estreita pra sobrar espaço pro
  // cabeçalho do paciente que a ficha teleporta pro slot central.
  const { density } = useDensity();
  const compact = density === 'compacto';
  // Dois comandos de tela, independentes entre si e do hambúrguer:
  //  - laterais: menu esquerdo + painel da agenda, sem tocar no cabeçalho;
  //  - foco (Alt+Z): as laterais E o cabeçalho da ficha, de uma vez.
  const { focus, toggleFocus, sidesCollapsed, toggleSides } = useFocusMode();

  // Sem a barra de busca (médico usa a gaveta lateral), a Topbar do médico
  // vira a casa do cabeçalho do paciente: altura AUTO (mínimo 48px) pra
  // acomodar o cabeçalho completo que a ficha teleporta pro slot central.
  const isMedico = role === Role.MEDICO;

  // Cabeçalho do paciente na barra + coluna apertada (laterais abertas, tela
  // de notebook): a direita cede espaço pro nome, em ordem — unidade, Agenda,
  // e por fim laterais/tela cheia/sino/Sair — tudo pro "⋯", até sobrar o que
  // a ficha disse que precisa (fichaHeaderFit).
  const slotNeed = useTopbarSlotNeed();
  const [setBarRef, barWidth] = useElementWidth<HTMLDivElement>();
  // Microfones (spec 016) e Ditar ficam sempre à vista; com a ficha na barra,
  // os microfones mostram só os pontos.
  const showMics = isVoiceEnabled(role);
  const collapse = topbarCollapseLevel(barWidth, slotNeed, showMics ? MICS_TOPO + DITAR_TOPO : 0);
  const agendaTarget = useAgendaChartTarget();
  const unitsInMenu = collapse >= 1 && units.length > 1 && accessibleUnits.length >= 1;
  const agendaInMenu = collapse >= 2 && !!agendaTarget.target;
  const restInMenu = collapse >= 3;

  return (
    <header className="bg-surface/80 backdrop-blur-glass border-b border-outline-variant/10 sticky top-0 z-40 shrink-0">
      <div ref={setBarRef} className={`${isMedico ? 'min-h-12 py-1' : 'h-16'} flex items-center justify-between px-4`}>
        <div className="flex items-center gap-4">
          <button onClick={onMenuClick} aria-label="Abrir menu" className="md:hidden p-2 -ml-2 text-on-surface-variant hover:text-on-surface">
            <Menu size={20} />
          </button>
          {/* Hambúrguer desktop — recolhe/expande SÓ o menu da esquerda.
              Escondido no mobile (lá o controle é o Menu acima, que abre o
              drawer). */}
          {onDesktopToggle && (
            <button
              type="button"
              onClick={onDesktopToggle}
              className="hidden md:inline-flex p-2 -ml-2 text-on-surface-variant hover:text-on-surface transition-colors"
              title={isDesktopCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
              aria-label={isDesktopCollapsed ? 'Expandir menu lateral' : 'Recolher menu lateral'}
              aria-expanded={!isDesktopCollapsed}
            >
              {isDesktopCollapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
            </button>
          )}
          {/* Barra fixa de busca — só pra recepção/secretária. O MÉDICO usa a
              gaveta lateral "Busca" (Ctrl+K / Alt+B) e ganha a faixa do topo
              de volta (PatientSearchDrawer, montada no Shell). */}
          {role !== Role.MEDICO && (
            <div className={`hidden md:block ${compact ? 'md:w-64 lg:w-72' : 'md:w-[26rem] lg:w-[30rem]'}`}>
              <GlobalPatientSearch />
            </div>
          )}
        </div>

        {/* Slot central — alvo do portal do cabeçalho do paciente (desktop).
            Preenche o espaço antes vazio entre a busca e os controles à
            direita; fica vazio nas demais páginas. */}
        {/* Médico não tem a busca na barra: o slot já cabe a partir do md (e
            o cabeçalho fica sempre à vista, inclusive no layout estreito). */}
        <div ref={setSlotRef} className={`hidden ${isMedico ? 'md:flex' : 'xl:flex'} flex-1 min-w-0 items-center px-3`} />

        <div className="flex items-center gap-3">
          {/* Unidade fixa: a clínica tem várias, mas esta conta só acessa uma
              (spec 019 — quem autoriza outras é admin/dono, em Equipe). */}
          {collapse < 1 && units.length > 1 && accessibleUnits.length === 1 && (
            <span
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-surface-container border border-outline-variant/20"
              title="Outras unidades: peça acesso ao administrador ou ao dono (Equipe)"
              data-testid="topbar-unit-fixed"
            >
              <Building2 size={14} className="text-primary" />
              <span className="hidden sm:inline text-on-surface-variant max-w-[120px] truncate">
                {accessibleUnits[0].name}
              </span>
            </span>
          )}

          {/* Unit switcher — só as unidades que esta conta acessa. */}
          {collapse < 1 && accessibleUnits.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setUnitDropdownOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-surface-container border border-outline-variant/20 hover:border-primary/40 transition-colors"
              >
                <Building2 size={14} className="text-primary" />
                <span className="hidden sm:inline text-on-surface-variant max-w-[120px] truncate">
                  {currentUnit?.name ?? 'Selecionar'}
                </span>
                <ChevronDown size={14} className="text-on-surface-variant" />
              </button>
              {unitDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUnitDropdownOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-surface border border-outline-variant/20 rounded-lg shadow-lg py-1 min-w-[180px]">
                    {accessibleUnits.map((unit) => (
                      <button
                        key={unit.id}
                        onClick={() => {
                          setCurrentUnitId(unit.id);
                          // Escolher uma unidade aqui tira a Agenda de "Todas
                          // as unidades" (spec 019) — senão a troca não teria
                          // efeito nenhum na tela.
                          setAllUnitsView(false);
                          setUnitDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-primary/5 transition-colors ${
                          unit.id === currentUnitId ? 'text-primary font-medium' : 'text-on-surface-variant'
                        }`}
                      >
                        {unit.name}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Mobile search toggle */}
          <button
            onClick={() => setMobileSearchOpen(v => !v)}
            className="md:hidden p-2 text-on-surface-variant hover:text-on-surface"
          >
            {mobileSearchOpen ? <X size={20} /> : <Search size={20} />}
          </button>

          {!isOnline && (
            <Badge variant="error" className="gap-1">
              <WifiOff size={12} />
              <span className="hidden sm:inline">Offline</span>
            </Badge>
          )}
          {syncPending && (
            <Badge variant="secondary" className="animate-pulse hidden sm:flex">Sincronizando...</Badge>
          )}

          {/* Microfones (spec 016): como está cada um agora e o "Ouvir os
              microfones" a um clique. Fica sempre à vista — com a ficha na
              barra mostra só os pontos (nomes no title e no painel). */}
          {showMics && <span className="hidden md:inline-flex"><MicsNoTopo compacto={slotNeed > 0} /></span>}

          {/* Ditar ao lado dos microfones (29/10/2026); no computador saiu do
              menu da esquerda. Só na ficha de um paciente. */}
          {showMics && <span className="hidden md:inline-flex"><TopbarDictateButton /></span>}

          {/* Agenda ⇄ ficha do paciente em atendimento (Alt+X). */}
          <AgendaChartToggle hidden={collapse >= 2} />

          {/* Par de controles de tela, no estilo dos players de vídeo: o
              quadrado recolhe/expande as LATERAIS (menu + painel da agenda);
              a pílula ao lado faz isso E o cabeçalho (tela cheia do cockpit).
              Cada um funciona sozinho — nenhum força o outro. Só desktop. */}
          {!restInMenu && (
          <div className="hidden md:flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleSides}
              aria-pressed={sidesCollapsed}
              data-dock-control
              className="grid h-9 w-9 place-items-center rounded-lg border border-outline-variant/40 text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
              title={sidesCollapsed ? 'Expandir menu lateral e painel da agenda' : 'Recolher menu lateral e painel da agenda'}
              aria-label={sidesCollapsed ? 'Expandir laterais' : 'Recolher laterais'}
            >
              {sidesCollapsed ? <ChevronsLeftRight size={17} /> : <ChevronsRightLeft size={17} />}
            </button>
            <button
              type="button"
              onClick={toggleFocus}
              aria-pressed={focus}
              data-dock-control
              className={`grid h-9 w-[3.25rem] place-items-center rounded-full transition-colors ${
                focus
                  ? 'bg-primary text-on-primary hover:bg-primary/90'
                  : 'bg-surface-container-high text-on-surface hover:bg-surface-container-highest'
              }`}
              title={focus ? 'Sair da tela cheia do cockpit (Alt+Z)' : 'Tela cheia do cockpit — recolhe laterais e cabeçalho (Alt+Z)'}
              aria-label={focus ? 'Sair da tela cheia' : 'Tela cheia do cockpit'}
            >
              {focus ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
          </div>
          )}

          {/* Sino — mostra briefings pendentes pra secretária/recepção.
              Quando não há nada, fica neutro (sem badge). Click leva pra
              Caixa de Pendências, onde a ação acontece. */}
          {!restInMenu && (
          <button
            type="button"
            onClick={() => navigate('/pendencias')}
            className={`p-2 relative transition-colors ${
              hasBriefings
                ? 'text-warning hover:text-warning/80'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
            title={
              hasBriefings
                ? `${briefingCount} atendimento${briefingCount === 1 ? '' : 's'} aguardando orientação`
                : 'Sem pendências de orientação'
            }
            aria-label="Pendências"
          >
            <Bell size={20} className={hasBriefings ? 'animate-pulse-slow' : ''} />
            {hasBriefings && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-on-error text-[10px] font-bold flex items-center justify-center leading-none tabular-nums">
                {briefingLabel}
              </span>
            )}
          </button>
          )}
          {!restInMenu && (
          <button onClick={logout} className="text-sm font-medium text-on-surface-variant hover:text-primary transition-colors">
            Sair
          </button>
          )}

          {(unitsInMenu || agendaInMenu || restInMenu) && (
            <TopbarMoreMenu
              dot={restInMenu && hasBriefings}
              units={unitsInMenu ? accessibleUnits : []}
              unitFixed={unitsInMenu && accessibleUnits.length === 1}
              currentUnitId={currentUnitId}
              onPickUnit={(unitId) => {
                setCurrentUnitId(unitId);
                setAllUnitsView(false);
              }}
              agenda={agendaInMenu ? agendaTarget : null}
              rest={
                restInMenu
                  ? {
                      sidesCollapsed,
                      toggleSides,
                      focus,
                      toggleFocus,
                      briefingLabel: hasBriefings ? briefingLabel : null,
                      openPendencias: () => navigate('/pendencias'),
                      logout,
                    }
                  : null
              }
            />
          )}
        </div>
      </div>

      {/* Mobile search bar */}
      {mobileSearchOpen && (
        <div className="md:hidden px-4 pb-3 animate-fade-in">
          <GlobalPatientSearch />
        </div>
      )}
    </header>
  );
};

const MENU_ITEM =
  'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm text-on-surface-variant hover:bg-primary/5 hover:text-on-surface transition-colors';

/**
 * "⋯" da barra do topo: guarda o que não coube ao lado do cabeçalho do
 * paciente (unidade, Agenda, laterais, tela cheia, pendências, Sair). Os
 * atalhos (Alt+X, Alt+Z) seguem valendo com os botões aqui dentro.
 */
const TopbarMoreMenu = ({
  dot,
  units,
  unitFixed,
  currentUnitId,
  onPickUnit,
  agenda,
  rest,
}: {
  dot: boolean;
  units: { id: string; name: string }[];
  unitFixed: boolean;
  currentUnitId: string | null | undefined;
  onPickUnit: (unitId: string) => void;
  agenda: ReturnType<typeof useAgendaChartTarget> | null;
  rest: {
    sidesCollapsed: boolean;
    toggleSides: () => void;
    focus: boolean;
    toggleFocus: () => void;
    briefingLabel: string | null;
    openPendencias: () => void;
    logout: () => void;
  } | null;
}) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clicou num item → faz e fecha.
  const act = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <div ref={boxRef} className="relative" data-dock-control>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Mais opções"
        title="Mais: unidade, agenda, tela, pendências, sair"
        data-testid="topbar-more"
        className="relative grid h-9 w-9 place-items-center rounded-lg border border-outline-variant/40 text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface"
      >
        <MoreHorizontal size={18} />
        {dot && <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-error" aria-hidden />}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-50 min-w-[230px] rounded-lg border border-outline-variant/20 bg-surface py-1 shadow-lg animate-fade-in"
        >
          {units.length > 0 && (
            <>
              <p className="px-3 pt-1.5 pb-1 label-mono">Unidade</p>
              {unitFixed ? (
                <p
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-on-surface-variant"
                  title="Outras unidades: peça acesso ao administrador ou ao dono (Equipe)"
                >
                  <Building2 size={15} className="text-primary shrink-0" />
                  {units[0].name}
                </p>
              ) : (
                units.map((unit) => (
                  <button
                    key={unit.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={unit.id === currentUnitId}
                    onClick={act(() => onPickUnit(unit.id))}
                    className={`${MENU_ITEM} ${unit.id === currentUnitId ? 'text-primary font-medium' : ''}`}
                  >
                    <Building2 size={15} className="text-primary shrink-0" />
                    <span className="flex-1 truncate">{unit.name}</span>
                    {unit.id === currentUnitId && <Check size={14} className="shrink-0" />}
                  </button>
                ))
              )}
              {(agenda || rest) && <div className="my-1 h-px bg-outline-variant/20" />}
            </>
          )}
          {agenda && (
            <button type="button" role="menuitem" onClick={act(agenda.go)} className={MENU_ITEM}>
              {agenda.onChart ? <CalendarDays size={15} className="shrink-0" /> : <Undo2 size={15} className="shrink-0" />}
              <span className="flex-1 truncate">{agenda.onChart ? 'Agenda' : agenda.shortName}</span>
              <kbd className="font-sans text-[11px] text-on-surface-variant/70">Alt X</kbd>
            </button>
          )}
          {rest && (
            <>
              <button type="button" role="menuitem" onClick={act(rest.toggleSides)} className={MENU_ITEM}>
                {rest.sidesCollapsed ? <ChevronsLeftRight size={15} className="shrink-0" /> : <ChevronsRightLeft size={15} className="shrink-0" />}
                <span className="flex-1">{rest.sidesCollapsed ? 'Expandir laterais' : 'Recolher laterais'}</span>
              </button>
              <button type="button" role="menuitem" onClick={act(rest.toggleFocus)} className={MENU_ITEM}>
                {rest.focus ? <Minimize2 size={15} className="shrink-0" /> : <Maximize2 size={15} className="shrink-0" />}
                <span className="flex-1">{rest.focus ? 'Sair da tela cheia' : 'Tela cheia'}</span>
                <kbd className="font-sans text-[11px] text-on-surface-variant/70">Alt Z</kbd>
              </button>
              <button type="button" role="menuitem" onClick={act(rest.openPendencias)} className={MENU_ITEM}>
                <Bell size={15} className="shrink-0" />
                <span className="flex-1">Pendências</span>
                {rest.briefingLabel && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-error text-on-error text-[10px] font-bold flex items-center justify-center leading-none tabular-nums">
                    {rest.briefingLabel}
                  </span>
                )}
              </button>
              <div className="my-1 h-px bg-outline-variant/20" />
              <button type="button" role="menuitem" onClick={act(rest.logout)} className={MENU_ITEM}>
                <LogOut size={15} className="shrink-0" />
                <span className="flex-1">Sair</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
